import type { Router, RouteLocationRaw } from "vue-router";
import type { WebMcpTranslation } from "@/tradutors/webmcp";

/**
 * Utilidades compartilhadas pelas tools WebMCP.
 *
 * Regras que valem para TODAS as tools e estão implementadas aqui:
 * - a saída é sempre `string` (a spec não aceita objeto): dados viram JSON
 *   compacto, ações viram frase no idioma da rota;
 * - erro nunca escapa como exceção — vira texto legível para o agente;
 * - URL devolvida é sempre absoluta e resolvida por NOME de rota, nunca
 *   concatenada à mão.
 */

/**
 * Contexto que toda factory de tools recebe, montado por `useWebMcpTools`.
 *
 * O `router` vem pronto de propósito: a factory é re-executada a cada troca de
 * idioma, dentro de um flush de watcher — fora do contexto do Nuxt, onde
 * `useRouter()` estouraria. Quem precisa de rota usa `ctx.router`.
 */
export interface WebMcpToolsContext {
  router: Router;
  /** Idioma da rota atual ("pt-br" | "en"). */
  language: string;
  /** Textos já resolvidos no idioma da rota. */
  t: WebMcpTranslation;
}

/** Dados estruturados de volta para o agente. */
export function jsonResult(value: unknown): string {
  return JSON.stringify(value);
}

/** URL absoluta a partir de um destino nomeado do vue-router. */
export function absoluteUrl(router: Router, to: RouteLocationRaw): string {
  return window.location.origin + router.resolve(to).href;
}

/**
 * Envolve o `execute` de uma tool. Faz duas coisas:
 *
 * 1. NORMALIZA OS ARGUMENTOS. O Chrome chama `execute()` com UM argumento só —
 *    verificado em Chrome/151.0.7922.75, onde `arguments.length === 1` e o
 *    segundo parâmetro chega `undefined`. Toda tool que declarava
 *    `(args, { signal })` estourava `TypeError: Cannot destructure property
 *    'signal' of 'undefined'`, que era engolido pelo catch abaixo e devolvido
 *    ao agente como "não foi possível concluir" — quebrando silenciosamente
 *    search_models, browse_catalog, list_categories, get_account_status,
 *    get_purchase_history, get_my_credits e get_credit_packages.
 *    Preenchendo o contexto aqui, num ponto só, as tools continuam podendo
 *    declarar `{ signal }` e o cancelamento segue funcionando quando (e se) o
 *    browser passar a mandar o contexto.
 * 2. Qualquer exceção (rede, 401, payload inesperado) vira a mensagem de erro
 *    traduzida, em vez de estourar no agente.
 */
export function safeExecute(
  t: WebMcpTranslation,
  name: string,
  execute: WebMcpToolDescriptor["execute"]
): WebMcpToolDescriptor["execute"] {
  return async (args, context) => {
    try {
      return await execute(
        args ?? {},
        context ?? { signal: new AbortController().signal }
      );
    } catch (error) {
      if (import.meta.dev) console.warn(`[webmcp] tool "${name}" falhou`, error);
      return t.messages.error;
    }
  };
}

/** Açúcar para declarar uma tool já com o safeExecute aplicado. */
export function defineTool(
  t: WebMcpTranslation,
  tool: WebMcpToolDescriptor
): WebMcpToolDescriptor {
  return { ...tool, execute: safeExecute(t, tool.name, tool.execute) };
}

/** Schema de objeto sem argumentos — as tools de leitura pura usam este. */
export const EMPTY_SCHEMA: Record<string, unknown> = {
  type: "object",
  properties: {},
};

/** Rotas de artigo. A de colunas leva o "catergoriaId" com o erro da pasta. */
export const ROTA_DO_POST = "language-blog-id-slug";
export const ROTA_DA_COLUNA =
  "language-colunas-catergoriaId-categoriaName-colunaId-colunaName";

/**
 * Blog e colunas vêm com SLOTS DE BANNER: itens com todos os campos null, nas
 * posições que ColumnsCards/Cards.vue renderiza como anúncio (índices 2 e 6).
 * Sem descartá-los, o router.resolve estoura "Missing required param".
 */
export function postDeVerdade(post: any): boolean {
  return post?.id != null && Boolean(post?.slug);
}

export function colunaDeVerdade(coluna: any): boolean {
  return (
    coluna?.id != null &&
    Boolean(coluna?.slug) &&
    coluna?.tag_id != null &&
    Boolean(coluna?.slugCategory)
  );
}

export function urlDoPost(router: Router, language: string, post: any): string {
  return absoluteUrl(router, {
    name: ROTA_DO_POST,
    params: { language, id: post.id, slug: post.slug },
  });
}

export function urlDaColuna(
  router: Router,
  language: string,
  coluna: any
): string {
  return absoluteUrl(router, {
    name: ROTA_DA_COLUNA,
    params: {
      language,
      catergoriaId: coluna.tag_id,
      categoriaName: coluna.slugCategory,
      colunaId: coluna.id,
      colunaName: coluna.slug,
    },
  });
}

/**
 * Corta texto longo antes de mandar ao agente: cada caractere ocupa contexto
 * dele. Corta em fim de palavra quando há uma perto do limite.
 */
export function textoLimitado(
  texto: string,
  limite: number
): { texto: string; cortado: boolean } {
  if (texto.length <= limite) return { texto, cortado: false };
  const corte = texto.slice(0, limite);
  const espaco = corte.lastIndexOf(" ");
  const base = espaco > limite * 0.8 ? corte.slice(0, espaco) : corte;
  return { texto: `${base.trimEnd()} …`, cortado: true };
}

/**
 * Corta o payload da API para o mínimo que o agente precisa. Nunca devolvemos
 * `cover`, `gif` nem `preview`: são mídias adultas, e o contrato das tools é
 * devolver só título e endereço de página.
 */
export function toCatalogItem(
  router: Router,
  language: string,
  routeName: string,
  item: { title?: string; slug?: string }
) {
  return {
    title: item.title ?? "",
    url: absoluteUrl(router, {
      name: routeName,
      params: { language, name: item.slug },
    }),
  };
}
